import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDomains } from "@/context/DomainContext";
import LeavesCanvas from "@/components/LeavesCanvas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Server, Users, UsersRound, Building2, Smartphone, ArrowLeftRight,
  ShieldOff, Share2, BookUser, ScrollText, UserCog, Leaf, LogOut, Menu, X, Sparkles, Settings, Mail,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/accounts", label: "Workspace Accounts", icon: Server },
  { to: "/users", label: "User Management", icon: Users, googleOnly: true },
  { to: "/mailboxes", label: "Mailboxes", icon: Mail },
  { to: "/groups", label: "Groups", icon: UsersRound, googleOnly: true },
  { to: "/org-units", label: "Org Units", icon: Building2, googleOnly: true },
  { to: "/devices", label: "Mobile Devices", icon: Smartphone, googleOnly: true },
  { to: "/migration", label: "File Migration", icon: ArrowLeftRight, googleOnly: true },
  { to: "/permissions", label: "Permissions", icon: ShieldOff, googleOnly: true },
  { to: "/sharing", label: "Cross-Domain Sharing", icon: Share2, googleOnly: true },
  { to: "/directory", label: "Directory & Roles", icon: BookUser, googleOnly: true },
  { to: "/audit", label: "Audit Log", icon: ScrollText },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/admins", label: "Portal Admins", icon: UserCog, adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { domains, selected, selectedId, select } = useDomains();
  const [leaves, setLeaves] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const isEmailProvider = selected?.provider === "migadu" || selected?.provider === "hostinger";

  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="relative min-h-screen">
      <LeavesCanvas enabled={leaves} />

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-full w-72 p-4 transition-transform duration-300 lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="glass flex h-full flex-col rounded-3xl p-5">
          <div className="mb-6 flex items-center gap-3 px-2">
            <div className="glossy-emerald relative flex h-11 w-11 items-center justify-center rounded-2xl">
              <Leaf className="h-6 w-6" />
              <span className="spec-shine" />
            </div>
            <div>
              <p className="font-heading text-lg font-bold leading-none text-slate-900">GSuite Ninja</p>
              <p className="text-xs text-slate-500">Workspace Command</p>
            </div>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto pr-1">
            {NAV.filter((n) => (!n.adminOnly || user?.role === "admin") && (!n.googleOnly || !isEmailProvider)).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className={({ isActive }) =>
                  cn(
                    "group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
                    isActive
                      ? "glossy-emerald relative shadow-md"
                      : "text-slate-600 hover:bg-white/70 hover:text-emerald-700"
                  )
                }
              >
                <n.icon className="h-[18px] w-[18px]" />
                <span>{n.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="mt-3 rounded-2xl bg-white/60 p-3 text-xs text-slate-500">
            <div className="flex items-center gap-2 font-medium text-emerald-700">
              <Sparkles className="h-3.5 w-3.5" /> {domains.length} domains connected
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="relative z-10 lg:pl-72">
        <header className="glass sticky top-0 z-30 mx-4 mt-4 flex items-center justify-between gap-3 rounded-2xl px-4 py-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="icon" className="lg:hidden"
              onClick={() => setOpen((o) => !o)} data-testid="sidebar-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div className="min-w-[190px]">
              <Select value={selectedId} onValueChange={select}>
                <SelectTrigger className="h-10 rounded-xl border-white/70 bg-white/70" data-testid="domain-switcher">
                  <SelectValue placeholder="Select a workspace" />
                </SelectTrigger>
                <SelectContent>
                  {domains.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No domains yet</div>}
                  {domains.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`domain-option-${d.domain}`}>
                      {d.label} · {d.domain}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="icon" className="rounded-xl"
              onClick={() => setLeaves((l) => !l)} data-testid="leaves-toggle-button"
              title="Toggle ambient leaves"
            >
              <Leaf className={cn("h-5 w-5", leaves ? "text-emerald-600" : "text-slate-400")} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="glossy-emerald relative flex h-10 w-10 items-center justify-center rounded-xl text-sm font-semibold" data-testid="profile-menu">
                  {initials}
                  <span className="spec-shine" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold text-slate-800">{user?.name}</p>
                  <p className="truncate text-xs text-slate-500">{user?.email}</p>
                  <p className="mt-1 inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">{user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
